import { Component, InfernoNode } from "inferno";
import { T } from "inferno-i18next-dess";
import {
  ApproveRegistrationApplication,
  MyUserInfo,
  RegistrationApplicationView,
} from "lemmy-js-client";
import { aimooT } from "@utils/aimoo-i18n";
import { mdToHtml } from "@utils/markdown";
import { I18NextService } from "../../services";
import { PersonListing } from "../person/person-listing";
import { Spinner } from "./icon";
import { MarkdownTextArea } from "./markdown-textarea";
import { MomentTime } from "./moment-time";

interface RegistrationApplicationProps {
  application: RegistrationApplicationView;
  myUserInfo: MyUserInfo | undefined;
  onApproveApplication: (form: ApproveRegistrationApplication) => void;
  loading: boolean;
}

interface RegistrationApplicationState {
  denyReason?: string;
  denyExpanded: boolean;
}

export class RegistrationApplication extends Component<
  RegistrationApplicationProps,
  RegistrationApplicationState
> {
  state: RegistrationApplicationState = {
    denyReason: this.props.application.registration_application.deny_reason,
    denyExpanded: false,
  };

  componentWillReceiveProps(
    nextProps: Readonly<
      { children?: InfernoNode } & RegistrationApplicationProps
    >,
  ) {
    if (this.props !== nextProps) {
      this.setState({
        denyExpanded: false,
      });
    }
  }

  render() {
    const a = this.props.application;
    const ra = this.props.application.registration_application;
    const accepted = a.creator_local_user.accepted_application;

    return (
      <div className="registration-application">
        <div className="aimoo-reg-app-meta">
          <div className="aimoo-reg-app-meta__row">
            <span className="aimoo-reg-app-meta__label">
              {I18NextService.i18n.t("applicant")}
            </span>
            <PersonListing
              person={a.creator}
              banned={false}
              myUserInfo={this.props.myUserInfo}
              muted={false}
            />
          </div>
          <div className="aimoo-reg-app-meta__row">
            <span className="aimoo-reg-app-meta__label">
              {I18NextService.i18n.t("created")}
            </span>
            <MomentTime showAgo published={ra.published_at} />
          </div>
          {a.creator_local_user.email && (
            <div className="aimoo-reg-app-meta__row">
              <span className="aimoo-reg-app-meta__label">
                {I18NextService.i18n.t("email")}
              </span>
              <a
                className="aimoo-reg-app-meta__email"
                href={`mailto:${a.creator_local_user.email}`}
              >
                {a.creator_local_user.email}
              </a>
            </div>
          )}
        </div>

        <div className="aimoo-reg-app-answer">
          <div className="aimoo-reg-app-answer__label">
            {I18NextService.i18n.t("answer")}
          </div>
          <div
            className="md-div aimoo-reg-app-answer__body"
            dangerouslySetInnerHTML={mdToHtml(ra.answer, () =>
              this.forceUpdate(),
            )}
          />
        </div>

        {a.admin && (
          <div className="aimoo-reg-app-review">
            {accepted ? (
              <T i18nKey="approved_by">
                #
                <PersonListing
                  person={a.admin}
                  banned={false}
                  myUserInfo={this.props.myUserInfo}
                  muted={false}
                />
              </T>
            ) : (
              <div>
                <T i18nKey="denied_by">
                  #
                  <PersonListing
                    person={a.admin}
                    banned={false}
                    myUserInfo={this.props.myUserInfo}
                    muted={false}
                  />
                </T>
                {ra.deny_reason && (
                  <div className="aimoo-reg-app-review__reason">
                    <span className="aimoo-reg-app-meta__label">
                      {I18NextService.i18n.t("deny_reason")}
                    </span>
                    <div
                      className="md-div"
                      dangerouslySetInnerHTML={mdToHtml(ra.deny_reason, () =>
                        this.forceUpdate(),
                      )}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {this.state.denyExpanded && (
          <div className="aimoo-reg-app-deny">
            <label className="aimoo-reg-app-deny__label">
              {I18NextService.i18n.t("deny_reason")}
            </label>
            <MarkdownTextArea
              initialContent={this.state.denyReason}
              onContentChange={val => handleDenyReasonChange(this, val)}
              hideNavigationWarnings
              allLanguages={[]}
              siteLanguages={[]}
              myUserInfo={this.props.myUserInfo}
              imageUploadDisabled
              placeholder={aimooT("deny_reason_placeholder")}
            />
          </div>
        )}

        <div className="aimoo-reg-app-actions">
          {(!ra.admin_id || (ra.admin_id && !accepted)) && (
            <button
              className="btn aimoo-reg-app-actions__approve"
              onClick={() => handleApprove(this)}
              aria-label={I18NextService.i18n.t("approve")}
              disabled={this.props.loading}
            >
              {this.props.loading ? (
                <Spinner />
              ) : (
                I18NextService.i18n.t("approve")
              )}
            </button>
          )}
          {(!ra.admin_id || (ra.admin_id && accepted)) && (
            <button
              className="btn aimoo-reg-app-actions__deny"
              onClick={() => handleDeny(this)}
              aria-label={I18NextService.i18n.t("deny")}
              disabled={this.props.loading}
            >
              {this.props.loading ? (
                <Spinner />
              ) : this.state.denyExpanded ? (
                aimooT("confirm_deny")
              ) : (
                I18NextService.i18n.t("deny")
              )}
            </button>
          )}
          {this.state.denyExpanded && (
            <button
              type="button"
              className="btn aimoo-reg-app-actions__cancel"
              onClick={() => this.setState({ denyExpanded: false })}
            >
              {aimooT("cancel")}
            </button>
          )}
        </div>
      </div>
    );
  }
}

function handleApprove(i: RegistrationApplication) {
  i.setState({ denyExpanded: false });
  i.props.onApproveApplication({
    id: i.props.application.registration_application.id,
    approve: true,
  });
}

function handleDeny(i: RegistrationApplication) {
  if (i.state.denyExpanded) {
    i.setState({ denyExpanded: false });
    i.props.onApproveApplication({
      id: i.props.application.registration_application.id,
      approve: false,
      deny_reason: i.state.denyReason,
    });
  } else {
    i.setState({ denyExpanded: true });
  }
}

function handleDenyReasonChange(i: RegistrationApplication, val: string) {
  i.setState({ denyReason: val });
}
